import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, Injector, Provider, inject, provideAppInitializer } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AuthService, ConfigurationService, TestAuthService, TestInterceptor } from 'shared';
import { environment } from '../../environments/environment';
import { computeRedirectUris, parseRuntimeMsalConfig } from './msal-config';

/**
 * Build the MSAL `PublicClientApplication` from the already-loaded
 * `ConfigurationService` (populated by an APP_INITIALIZER in main.ts before
 * any DI consumer can construct MSAL).
 */
export function MSALInstanceFactory(
  configurationService: ConfigurationService,
): IPublicClientApplication {
  const cfg = parseRuntimeMsalConfig(configurationService.getConfiguration());
  if (!cfg) {
    throw new Error('[MSAL] /api/configuration is missing client_id or authority.');
  }

  // Derive redirect URIs from <base href> so they match the deployment path
  // automatically ("/" in dev, "/service/" in prod).
  const { redirectUri, postLogoutRedirectUri } = computeRedirectUris(document.baseURI);

  return new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: cfg.authority,
      knownAuthorities: cfg.knownAuthorities,
      redirectUri,
      postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string) => {
          if (level === LogLevel.Error) {
            console.error(`[MSAL] ${message}`);
          } else if (level === LogLevel.Warning) {
            console.warn(`[MSAL] ${message}`);
          }
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(
  configurationService: ConfigurationService,
): MsalGuardConfiguration {
  const cfg = parseRuntimeMsalConfig(configurationService.getConfiguration());
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: cfg?.scopes ?? [],
    },
    loginFailedRoute: '/',
  };
}

export function MSALInterceptorConfigFactory(
  configurationService: ConfigurationService,
): MsalInterceptorConfiguration {

  const { clientScope } = configurationService.getConfiguration();

  const protectedResourceMap = new Map<string, string[]>();

  const addProtectedBase = (base: string) => {
    const trimmed = base.replace(/\/$/, '');
    protectedResourceMap.set(`${trimmed}/api/configuration`, []);
    protectedResourceMap.set(`${trimmed}/api/*`, [clientScope]);
  };

  addProtectedBase(environment.serverUrl || window.location.origin);

  // Several services build their URLs as:
  //   `${environment.deploymentUrl ?? `https://${location.hostname}`}/api/...`
  // (e.g. gis.service, heat-meter.service, service-pipe.service, service-case.service).
  // In environments where DEPLOYMENT_URL is not injected, `environment.deploymentUrl`
  // resolves to null and the services fall back to window.location.origin.
  // We must protect that same fallback here, otherwise MsalInterceptor finds no
  // matching scope and sends the request without a Bearer token.
  addProtectedBase(environment.getiGridCustomerPortalUrl(window.location) || window.location.origin);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * MSAL providers used in production / staging. Factories depend on
 * `ConfigurationService` having already loaded `/api/configuration` via
 * `provideMsalAuthInitializer()`.
 */
const msalCoreProviders: Provider[] = [
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory,
    deps: [ConfigurationService],
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory,
    deps: [ConfigurationService],
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory,
    deps: [ConfigurationService],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true,
  },
  MsalService,
  MsalBroadcastService,
  MsalGuard,
];

/**
 * Test-mode providers: bypass MSAL entirely and swap in the stub
 * `TestAuthService` + `TestInterceptor` so e2e specs don't trigger a real
 * login redirect.
 */
const testModeProviders: Provider[] = [
  { provide: AuthService, useClass: TestAuthService },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: TestInterceptor,
    multi: true,
  },
  {
    provide: MsalGuard,
    useValue: {
      canActivate: () => true,
      canActivateChild: () => true,
      canMatch: () => true,
    },
  },
];

/**
 * APP_INITIALIZER that loads `/api/configuration` (via `HttpBackend`, so it
 * bypasses MsalInterceptor) and then constructs + initializes the MSAL
 * `PublicClientApplication`. Returning a Promise blocks Angular bootstrap
 * until both are ready, which guarantees that:
 *
 *   - the MSAL factory sees real `clientId` / `authority` values, and
 *   - any consumer of `MsalService` (interceptor, guard, AuthService) gets a
 *     fully-initialized instance with the redirect response already handled.
 *
 * In `testMode` we resolve immediately and stub providers replace MSAL.
 */
export function provideMsalAuthInitializer(): EnvironmentProviders {
  return provideAppInitializer(() => {
    if (environment.testMode) {
      return Promise.resolve();
    }

    // Resolve dependencies synchronously while we are still inside the
    // injection context. After the first `await`, calls to `inject()` would
    // fall outside it and throw NG0203.
    //
    // ConfigurationService can be injected directly — it has no dependency on
    // MSAL. MsalService / AuthService are resolved lazily via the Injector
    // AFTER the config load completes, because the MSAL_INSTANCE factory
    // reads `configurationService.getConfiguration()` synchronously.
    const injector = inject(Injector);
    const configurationService = inject(ConfigurationService);

    return (async () => {
      await configurationService.LoadConfiguration();

      // Resolving MsalService now triggers MSAL_INSTANCE construction via the
      // factory above — the loaded configuration is available to it.
      const msalService = injector.get(MsalService);
      try {
        await msalService.instance.initialize();
        await msalService.instance.handleRedirectPromise();
        const accounts = msalService.instance.getAllAccounts();
        if (accounts.length > 0 && !msalService.instance.getActiveAccount()) {
          msalService.instance.setActiveAccount(accounts[0]);
        }
      } catch (err) {
        console.error('[MSAL] initialize() / handleRedirectPromise failed', err);
      }

      // Eager-resolve AuthService so it subscribes to MsalBroadcastService
      // and keeps `currentUser` / `authenticated` in sync from app start.
      injector.get(AuthService);
    })();
  });
}

/**
 * Aggregated MSAL providers for `main.ts`. Order matters: app-initializer
 * runs first to load config + initialize MSAL, then the MSAL DI providers
 * become resolvable.
 */
export function provideMsalAuth(): Array<Provider | EnvironmentProviders> {
  if (environment.testMode) {
    return [provideMsalAuthInitializer(), ...testModeProviders];
  }
  return [provideMsalAuthInitializer(), ...msalCoreProviders];
}
